/**
 * mergeTagsService — the editor-agnostic source of truth for merge tag handling
 * across every editing surface (SMS/RCS/MMS, GrapesJS email, subject/preheader).
 *
 * The service models only what the backend renderer provides (catalog), how
 * tags serialize (parse/build), the per-tag filter capabilities (filters), and
 * agnostic detection of unsupported-in-broadcast patterns (detection). It has
 * **no dependency on any `src/features` module** — feature-specific authoring
 * concerns (aliases, product-grid resolution, pickers) live with the feature.
 *
 * See `ARCHITECTURE.md` for the module map and `README.md` for "how to add a
 * merge tag" / "how to add a filter".
 */

import { build, ifElseWrapperBuilder } from "./build";
import {
  find,
  list,
  type ListOptions,
  type MergeTagAvailability,
  type MergeTagCategory,
  type MergeTagDefinition,
} from "./catalog";
import { hasProductContextTags } from "./detection";
import {
  list as listFilters,
  normalize as normalizeFilters,
  previewTextCase,
  resolve as resolveFilters,
} from "./filters";
import { matchFirstMergeTag, parse } from "./parse";

export const mergeTagsService = {
  /** Catalog grouped by category, optionally filtered by surface/category. */
  list,
  /** Look up a catalog definition by identifier. */
  find,
  /** Parse a `{{ ... }}` inner string into `{ identifier, filters }`. */
  parse,
  matchFirstMergeTag,
  /** Build wire syntax for an identifier + resolved filters. */
  build,
  ifElseWrapperBuilder,
  filters: {
    list: listFilters,
    resolve: resolveFilters,
    normalize: normalizeFilters,
    previewTextCase,
  },
  detection: {
    hasProductContextTags,
  },
} as const;

export type {
  ListOptions,
  MergeTagAvailability,
  MergeTagCategory,
  MergeTagDefinition,
};

// ---------------------------------------------------------------------------
// Direct module re-exports for call sites that consume primitives directly.
// ---------------------------------------------------------------------------

export {
  CATALOG,
  PRODUCT_COLLECTIONS,
  PRODUCT_FIELDS,
  productFieldIdentifier,
  productFieldOf,
  type ProductFieldTemplate,
} from "./catalog";

export { hasProductContextTags } from "./detection";

export {
  ACTIVE_BUILDER,
  build,
  ifElseWrapperBuilder,
  liquidFilterChainBuilder,
  type MergeTagBuilder,
} from "./build";

export {
  FALLBACK_VALUE_MAX_LENGTH,
  FILTER_ORDER,
  FILTER_REGISTRY,
  normalize as normalizeFilters,
  previewPositiveOnly,
  previewTextCase,
  RATING_DISPLAY_OPTIONS,
  sanitizeFallback,
  TEXT_CASE_OPTIONS,
  type MergeTagFilterControl,
  type MergeTagFilterDefinition,
  type MergeTagFilterId,
  type MergeTagFilterOption,
  type ResolvedFilters,
} from "./filters";

export {
  formatMergeTagSlot,
  matchFirstMergeTag,
  MERGE_TAG_IF_ELSE_PATTERN,
  MERGE_TAG_PATTERN,
  MERGE_TAG_SLOT_PATTERN,
  parse,
  parseMergeTagSlot,
  type MergeTagOccurrence,
  type ParsedMergeTagSlot,
} from "./parse";

export { isSurface, SURFACES, type Surface } from "./surfaces";

export {
  collectCustomerMetadataTags,
  collectEventMergeTags,
  customerMetadataTagsToCategories,
  type CustomerMetadataTagInput,
} from "./customerMetadataTags";

export {
  GENERATED_CONTENT_FIELDS,
  GENERATED_CONTENT_PREFIX,
  generatedContentIdentifier,
  getGeneratedContentField,
  parseGeneratedContentIdentifier,
  type GeneratedContentField,
} from "./generatedContent";
